import { DefaultResources } from '@/data/resourceData';
import { getEntries, getKeys } from '@/helpers';
import type { ResourceFilterKey } from '@/interfaces/resourceFilterInterface';
import type { GetResourcesResponse, Resource } from '@/interfaces/resourceInterface';
import type { Term } from '@/interfaces/termInterface';
import { request, type RequestResponse } from '@/services/requestService';
import { searchWorker } from '@/workers/searchWorker';
import { defineStore } from 'pinia';
import { useUser } from './userStore';

export const useResourceStore = defineStore('resource', {
	state: () => {
		return {
			loading: false,
			resources: [] as Resource[],
			cache: 0,
			// The school id the currently cached resources were loaded for. The
			// server-side cache epoch is global, but the resource payload varies
			// per school (parent videos are filtered by the school's EF), so we
			// only trust the cache when we're still on the same school.
			cachedSchoolId: 0,
			// The app version (X-App-Version) the cached resources were loaded on.
			// The content epoch doesn't bump on deploys, so a deploy that changes
			// the resource payload shape is detected here and invalidates the cache
			// (see checkBackendVersion in requestService).
			appVersion: '',
			categoryFilter: '',
			filters: {} as { [key in ResourceFilterKey]: string },
			currentSearch: '',
			rawSearch: '',
			searching: false,
			searchedAndFilteredResources: [] as Resource[],
			searchUnsubscribeHandler: () => {},
			enableActionPlanFilter: false,
			actionPlanResourceIds: DefaultResources,
		};
	},
	getters: {
		categories(): Term[] {
			if (this.resources.length <= 0) {
				return [];
			}

			const categories = this.resources
				.reduce((acc, r) => {
					r.categories.forEach((c) => {
						if (!acc.some((a) => a.slug === c.slug)) {
							acc.push(c);
						}
					});

					return acc;
				}, [] as Term[])
				.sort((a, b) => a.name.localeCompare(b.name));

			return categories;
		},
		loadingWithNoResources(state): boolean {
			return state.loading && state.resources.length <= 0;
		},
		// The current school's favourited resource IDs. Favourites are per-school
		// state that lives on the user (not on the shared resource objects), so we
		// derive "is this favourited" from here rather than mutating resources.
		currentSchoolFavourites(): number[] {
			const user = useUser();
			const schoolId = user.school?.ID ?? 0;
			const list = user.resourceFavourites[schoolId];
			return Array.isArray(list) ? list : [];
		},
		isResourceFavourited(): (resourceId: number) => boolean {
			return (resourceId: number) => this.currentSchoolFavourites.includes(resourceId);
		},
		favouriteResources(state): Resource[] {
			return state.resources.filter((r) => this.isResourceFavourited(r.ID));
		},
		filteredAndSearchedResources(state): Resource[] {
			if (!state.currentSearch) {
				return this.filteredResources;
			}

			return this.searchedAndFilteredResources;
		},
		filteredResources(state): Resource[] {
			const resources = state.resources
				.filter((r: Resource) => {
					let show = true;

					if (!r.searchable) {
						return false;
					}

					if (state.categoryFilter) {
						if (!r.categories.some((c) => c.slug === state.categoryFilter)) {
							return false;
						}
					}

					getEntries(state.filters).forEach(([taxSlug, filterByTermSlug]) => {
						if (!filterByTermSlug) {
							return;
						}

						show &&= r[taxSlug] === filterByTermSlug;
					});

					if (this.enableActionPlanFilter) {
						show &&= this.actionPlanResourceIds.indexOf(r.ID) >= 0;
					}

					return show;
				})
				.sort((r1, r2) => {
					if (this.isResourceFavourited(r1.ID)) {
						return -1;
					}

					if (this.isResourceFavourited(r2.ID)) {
						return 1;
					}

					let r1index = this.actionPlanResourceIds.indexOf(r1.ID);
					let r2index = this.actionPlanResourceIds.indexOf(r2.ID);
					if (r1index || r2index) {
						if (r1index === -1) {
							r1index = 999;
						}
						if (r2index === -1) {
							r2index = 999;
						}

						return r1index - r2index;
					}

					return 0;
				});

			return resources;
		},
	},
	actions: {
		handleCategoryFilterUpdate(categorySlug: string) {
			this.categoryFilter = categorySlug;

			if (this.currentSearch) {
				this.runStringSearch();
			}
		},
		handleFilterInputUpdate(taxSlug: ResourceFilterKey, val: string) {
			this.filters[taxSlug] = val;

			if (this.currentSearch) {
				this.runStringSearch();
			}
		},
		handleSearchInputUpdate(val: string) {
			this.rawSearch = val;
			const search = this.rawSearch.trim();

			if (search.length < 3) {
				this.currentSearch = '';
				this.searchedAndFilteredResources = [];
				return;
			} else {
				this.currentSearch = search;
				this.runStringSearch();
			}
		},
		async runStringSearch() {
			this.searching = true;
			this.searchedAndFilteredResources = await searchWorker.filterResources(this.currentSearch, this.filteredResources);
			this.searching = false;
		},
		async loadResources() {
			if (this.loading) {
				return;
			}

			let res: RequestResponse<GetResourcesResponse>;

			const auth = useUser();
			const currentSchoolId = auth.school?.ID ?? 0;

			// Portal access, not a token, is what decides whether the whole library is
			// fetched. The backend hands every resource to any authenticated caller, so a
			// user who has been removed from their school — who may still open a resource
			// link they've been sent — takes the single-resource branch below instead.
			if (auth.canAccessPortal) {
				// The cached resource payload is school-specific (parent videos are
				// filtered server-side by the school's EF), while the cache epoch is
				// global. So if the school changed since we cached, invalidate the
				// cache to force a fresh fetch for the new school.
				if (currentSchoolId !== this.cachedSchoolId) {
					this.cache = 0;
				}

				this.loading = true;
				res = await request<GetResourcesResponse>('publicResource', 'get', {
					cache: this.cache,
				});
			} else {
				await this.$router.isReady();
				const name = this.$router.currentRoute.value.name;
				const slug = this.$router.currentRoute.value.params.slug;

				if (name !== 'resourceSingle' || !slug) {
					return;
				}

				this.loading = true;

				res = await request<GetResourcesResponse>('publicResource', 'get', {
					slug,
				});
			}

			// Clear this before the early returns below, not after them: it also gates
			// re-entry at the top, so a failed load that left it true would stop
			// resources ever loading again this page session.
			this.loading = false;

			if (!res.success) {
				return;
			}

			if (res.data.cacheHit) {
				return;
			}

			this.resources = res.data.resources;
			this.cache = res.data.cache;
			this.cachedSchoolId = currentSchoolId;

			this.resetFilters();
		},
		resetFilters() {
			getKeys(this.filters).forEach((taxSlug) => {
				this.filters[taxSlug] = '';
			});
			this.categoryFilter = '';
			this.enableActionPlanFilter = false;
			this.rawSearch = '';
			this.currentSearch = '';
			this.runStringSearch();
		},
		getResource(rId: number): Resource | undefined {
			return this.resources.find((r) => r.ID === rId);
		},
		async toggleResourceFavourite(resource: Resource) {
			const user = useUser();
			const schoolId = user.school?.ID ?? 0;

			// Favourites live on the user (per school), keyed by school id, and the
			// UI derives "favourited" from this list. Updating it here — rather than
			// mutating the shared resource object — is what makes the change survive
			// a refresh, since it's the persisted source of truth. We don't bother
			// reverting on request failure: it mirrors a trivial backend toggle that
			// essentially never fails, and the next reload reconciles either way.
			const favourites = Array.isArray(user.resourceFavourites[schoolId]) ? [...user.resourceFavourites[schoolId]] : [];
			const i = favourites.indexOf(resource.ID);
			if (i >= 0) {
				favourites.splice(i, 1);
			} else {
				favourites.push(resource.ID);
			}
			user.resourceFavourites[schoolId] = favourites;

			await request('resource', 'toggleFavourite', {
				resourceId: resource.ID,
			});
		},
	},
	debounce: {
		runStringSearch: 300,
	},
	// Persist the loaded resources across refreshes so the /resources section
	// renders instantly instead of waiting for a fetch. On reload we send the
	// persisted `cache` epoch; the backend returns cacheHit (no payload) when
	// nothing has changed, so we only re-download when resources actually change
	// or the school changes. Only the serialisable data is persisted — never the
	// transient/loading state or the searchUnsubscribeHandler function.
	persist: {
		key: 'rs-resources-v1',
		pick: ['resources', 'cache', 'cachedSchoolId', 'appVersion'],
	},
});
